import { useState } from 'react';
import { MarkdownEditor } from '@/components/prompt-graph/markdown-editor';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Eye, Pencil, Code } from 'lucide-react';

type EditorMode = 'edit' | 'view' | 'raw';

export type PromptMarkdownTabProps = {
    value: string;
    onChange: (next: string) => void;
    isLoading: boolean;
    hasWorkingRevision: boolean;
    isArchived: boolean;
    /** Shown while content is loading. @default "Loading prompt…" */
    loadingLabel?: string;
    /** aria-label for the mode toggle. @default "Prompt editor mode" */
    editorAriaLabel?: string;
    /** Placeholder for the raw textarea. */
    rawPlaceholder?: string;
    /** Placeholder for the rich markdown editor. */
    editorPlaceholder?: string;
};

export function PromptMarkdownTab({
    value,
    onChange,
    isLoading,
    hasWorkingRevision,
    isArchived,
    loadingLabel = 'Loading prompt…',
    editorAriaLabel = 'Prompt editor mode',
    rawPlaceholder = 'Write your prompt markdown here…',
    editorPlaceholder = 'Write your prompt content here…',
}: PromptMarkdownTabProps) {
    const [mode, setMode] = useState<EditorMode>('edit');

    if (isLoading) {
        return (
            <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
                {loadingLabel}
            </div>
        );
    }

    const canEdit = hasWorkingRevision && !isArchived;
    const activeMode: EditorMode = canEdit ? mode : 'view';
    const rawRows = Math.max(20, value.split('\n').length + 2);

    return (
        <div className="space-y-3 px-24">
            {/* Mode toggle */}
            <div className="flex items-center justify-end">
                <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    value={activeMode}
                    onValueChange={(v) => {
                        if (v === 'view') { setMode(v); return; }
                        if (!canEdit) return;
                        if (v === 'edit' || v === 'raw') setMode(v);
                    }}
                    aria-label={editorAriaLabel}
                >
                    <ToggleGroupItem
                        value="edit"
                        disabled={!canEdit}
                        className="gap-1.5 px-3"
                    >
                        <Pencil size={14} />
                        Edit
                    </ToggleGroupItem>
                    <ToggleGroupItem value="view" className="gap-1.5 px-3">
                        <Eye size={14} />
                        View
                    </ToggleGroupItem>
                    <ToggleGroupItem
                        value="raw"
                        disabled={!canEdit}
                        className="gap-1.5 px-3"
                    >
                        <Code size={14} />
                        Raw
                    </ToggleGroupItem>
                </ToggleGroup>
            </div>

            {activeMode === 'raw' ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={rawRows}
                    spellCheck={false}
                    className="w-full rounded-lg border border-slate-200 bg-white p-4 font-mono text-sm leading-relaxed text-slate-700 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
                    placeholder={rawPlaceholder}
                />
            ) : (
                <MarkdownEditor
                    key={`${canEdit}-${activeMode}`}
                    value={value}
                    onChange={activeMode === 'edit' ? onChange : undefined}
                    readOnly={activeMode === 'view'}
                    minHeight="420px"
                    placeholder={editorPlaceholder}
                />
            )}
        </div>
    );
}
